import { SearchOutlined, FilterFilled, DownOutlined } from '@ant-design/icons'
import { Vertical, Texto, Horizontal, GraviButton } from '@gravitate-js/excalibrr'
import { Select } from 'antd'
import { type MapFilters, type PoiTypes, MapFilterKeys, type SiteSearchOption, SiteOnlyKeys } from '../types'
import type { getSearchOptions } from '../util'
import { useState } from 'react'
import { startCase } from 'lodash'

type FilterOptionType = ReturnType<typeof getSearchOptions>

type FilterDropdownProps = {
  name: PoiTypes
  filterOptions: FilterOptionType
  handleFilterChange: <T extends PoiTypes>(
    poiType: T,
    filterKey: keyof MapFilters[T],
    value: MapFilters[T][keyof MapFilters[T]]
  ) => void
  handleLocationSearch: (coords: [number, number]) => void
  poiFilters: MapFilters[PoiTypes]
  areFiltersActive: boolean
}

export function FilterDropdown({
  name,
  filterOptions,
  handleFilterChange,
  handleLocationSearch,
  poiFilters,
  areFiltersActive,
}: FilterDropdownProps) {
  const [showFilters, setShowFilters] = useState(areFiltersActive)

  function handleClearFilters() {
    if (name === 'sites') {
      handleFilterChange<'sites'>(name, 'counterparty_name', [])
      handleFilterChange<'sites'>(name, 'customer_name', [])
    }
    handleFilterChange(name, 'sector', [])
    handleFilterChange(name, 'state', [])
    handleFilterChange(name, 'city', [])
    handleFilterChange(name, 'postal_code', [])
  }

  function handleSearchChange(value: string, option: FilterOptionType[PoiTypes]['search'][number]) {
    try {
      handleLocationSearch(option.coords as [number, number])
    } catch (error) {
      console.error('Invalid value format:', value, error)
    }
  }

  return (
    <Vertical
      style={{
        backgroundColor: 'var(--bg-1)',
        borderRadius: '8px',
        boxShadow: '0 2px 2px rgba(60,64,67,0.3),0 1px 3px 1px rgba(60,64,67,0.15)',
        width: '250px',
      }}
      className='p-2'
      gap={6}
    >
      <Select
        showSearch
        placeholder={`Search ${startCase(name)}`}
        allowClear
        options={filterOptions[name].search}
        suffixIcon={<SearchOutlined />}
        onChange={handleSearchChange}
        optionRender={(option) => {
          const optionData = (option.data || {}) as SiteSearchOption
          if (name === 'sites')
            return (
              <Vertical>
                <Texto>
                  {optionData.locationName} {optionData.siteName}
                </Texto>
                <Texto category='label' appearance='hint'>
                  {optionData.counterparty}
                </Texto>
              </Vertical>
            )
          return <Texto>{option.data.label}</Texto>
        }}
        filterOption={(input, option) => {
          const label = option?.label?.toString().toLowerCase() || ''
          return label.includes(input.toLowerCase())
        }}
      />
      <Vertical height={'auto'} style={{ backgroundColor: 'var(--gray-100)', borderRadius: '4px' }}>
        <Horizontal
          justifyContent='space-between'
          alignItems='center'
          className='filter-header p-2'
          onClick={() => setShowFilters(!showFilters)}
        >
          <Texto>
            <FilterFilled size={18} className='mr-1' />
            Filters
          </Texto>
          <Vertical
            flex={'none'}
            style={{
              transition: 'transform 0.3s ease',
              transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <DownOutlined style={{ fontSize: '12px' }} />
          </Vertical>
        </Horizontal>

        <Vertical
          gap={8}
          height={'auto'}
          style={{
            maxHeight: showFilters ? '300px' : 0,
            overflow: 'hidden',
            transition: 'all 0.3s ease-in-out, opacity 0.2s ease',
          }}
          className={`px-2 ${showFilters ? 'py-2' : ''}`}
        >
          {MapFilterKeys.map((filterKey) => {
            if ((name === 'sites' && SiteOnlyKeys.includes(filterKey)) || !SiteOnlyKeys.includes(filterKey)) {
              return (
                <FilterOption
                  key={filterKey}
                  poiType={name}
                  filterKey={filterKey as keyof MapFilters[PoiTypes]}
                  filterOptions={filterOptions}
                  handleFilterChange={handleFilterChange}
                  poiFilters={poiFilters}
                />
              )
            }
          })}
          <Horizontal justifyContent='flex-end'>
            {areFiltersActive && (
              <GraviButton appearance='text' size='small' onClick={handleClearFilters}>
                Clear
              </GraviButton>
            )}
          </Horizontal>
        </Vertical>
      </Vertical>
    </Vertical>
  )
}

type FilterOptionProps = {
  poiType: PoiTypes
  filterKey: keyof MapFilters[PoiTypes]
  filterOptions: ReturnType<typeof getSearchOptions>
  handleFilterChange: <T extends PoiTypes>(
    poiType: T,
    filterKey: keyof MapFilters[T],
    value: MapFilters[T][keyof MapFilters[T]]
  ) => void
  poiFilters: MapFilters[PoiTypes]
}

function FilterOption({ poiType, filterKey, filterOptions, handleFilterChange, poiFilters }: FilterOptionProps) {
  if (filterOptions[poiType][filterKey]?.length === 0) {
    return null
  }

  const label = startCase(filterKey.replace('_', ' '))
  return (
    <Select
      mode='multiple'
      placeholder={`Select ${label}`}
      options={filterOptions[poiType][filterKey]}
      showSearch
      onChange={(value) => handleFilterChange(poiType, filterKey, value)}
      value={poiFilters?.[filterKey] || []}
    />
  )
}
